import type {
  ModelPortfolioTypes,
  PortfolioReportQueryFilter,
} from '@generated/graphql';
import { useMemo } from 'react';
import { STATUS_FILTER_MAP } from '../utils/portfolioExportConfig';
import {
  resolvePortfoliosListContext,
  type ViewMode,
} from '../utils/portfolioFilters';
import { getServerPagination } from '../utils/portfolioTabPagination';

export type UsePortfolioListQueryParams = {
  organizationId?: string;
  page: number;
  pageSize: number;
  debouncedSearch: string;
  statusFilter: string;
  portfolioTypeFilter: string;
  modelFilter: string[];
  viewMode: ViewMode;
  canReadRepCode: boolean;
  sortField: string;
  sortDesc: boolean;
};

export function usePortfolioVariables(params: UsePortfolioListQueryParams) {
  const {
    viewMode,
    debouncedSearch,
    modelFilter,
    page,
    pageSize,
    statusFilter,
    portfolioTypeFilter,
    organizationId,
    canReadRepCode,
    sortField,
    sortDesc,
  } = params;

  const {
    debouncedSearch: activeSearch,
    modelFilter: activeModelFilter,
    isClientFilter,
  } = resolvePortfoliosListContext(viewMode, {
    debouncedSearch,
    modelFilter,
  });

  const trimmedSearch = activeSearch.trim();

  const variables = useMemo(() => {
    const statusFilterKey = STATUS_FILTER_MAP[statusFilter];
    const serverPagination = getServerPagination({
      page,
      pageSize,
      isClientFilter,
    });

    const filter: PortfolioReportQueryFilter & { searchText?: string } = {
      organizationId,
      ...(statusFilterKey && { [statusFilterKey]: true }),
      modelPortfolioType:
        portfolioTypeFilter !== 'ANY'
          ? (portfolioTypeFilter as ModelPortfolioTypes)
          : undefined,
      modelPortfolioIds:
        activeModelFilter.length > 0 ? activeModelFilter : undefined,
      ...(trimmedSearch ? { searchText: trimmedSearch } : {}),
    };

    return {
      input: {
        filter,
        pagination: {
          sortField,
          sortDesc,
          ...serverPagination,
        },
      },
      canReadRepCode,
    };
  }, [
    organizationId,
    statusFilter,
    portfolioTypeFilter,
    activeModelFilter,
    page,
    pageSize,
    isClientFilter,
    canReadRepCode,
    trimmedSearch,
    sortField,
    sortDesc,
  ]);

  return { variables, isClientFilter };
}
